// Repo detail / artifact browser.
// Drill-down from /repos — shows the repo card, then a paginated
// list of artifacts (path, version, size, sha256, uploaded_at).
//
// v124 — repo properties are editable in place. Click "Edit" on the
// header to swap the read-only summary for a form; save calls the
// PATCH /api/v1/repos/{id} endpoint and re-fetches the SWR cache.
// Slug, kind, format, upstreamUrl, name, and (for virtuals) members
// are all editable. id and projectId are not.
//
// v186 — project-owner-editable retention card. Mounts under the
// repo header and talks to /api/v1/repos/{id}/retention. The
// endpoint 403s for non-maintainers; the card hides itself in that
// case so non-owners don't see an empty edit form they can't use.
//
// v190 — per-repo Stats tab (roadmap item 14). The tab switcher
// lives at the page top once a repo loads; "Contents" keeps the
// existing artifact table + retention card surface, "Stats" loads
// the 30-day pull / storage / consumer rollup. Tab state is in
// `?tab=stats` so deep links stay shareable. Stats data comes from
// /api/v1/repos/{id}/stats; the principal_email columns are only
// populated for maintainer+ callers (backend gates this) and the
// "Top consumers" tile collapses cleanly when empty.

import { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Box, Calendar, Copy, Download, FolderOpen, GitBranch, Pencil, Trash2, X } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import {
  createRepoRetention,
  deleteRepo,
  deleteRepoRetention,
  updateRepo,
  updateRepoRetention,
  useProjects,
  useRepoArtifacts,
  useRepoDetail,
  useRepoRetention,
  useRepoStats,
  type Repo,
  type RepoPatch,
  type RepoStats,
  type RetentionPolicy,
} from '../api/client';
import { type DockerImageTag, useDockerImages } from '../api/docker';
import { FORMATS } from '../lib/formats';
import { MemberSelector } from '../components/MemberSelector';

const KINDS: Repo['kind'][] = ['local', 'remote', 'virtual'];
const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function kindTone(kind: Repo['kind']): 'accent' | 'nebula' | 'neutral' {
  if (kind === 'remote') return 'nebula';
  if (kind === 'virtual') return 'accent';
  return 'neutral';
}

interface EditFormProps {
  readonly repo: Repo;
  readonly onSaved: (next: Repo) => void;
  readonly onCancel: () => void;
}

function EditForm({ repo, onSaved, onCancel }: EditFormProps) {
  // Local form state seeded from the loaded repo. We send the whole
  // surface to PATCH on save (the backend will no-op fields that
  // didn't change). Members come back as undefined for non-virtuals;
  // normalise to [] so the MemberSelector has a stable input.
  const [draft, setDraft] = useState<Repo>({
    ...repo,
    members: repo.members ?? [],
    upstreamUrl: repo.upstreamUrl ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // Send only the editable fields. id + projectId never move; the
      // backend rejects them silently. Strip members on non-virtual
      // kinds so the backend's "members on non-virtual" guard is
      // never triggered by stale state.
      const patch: RepoPatch = {
        slug: draft.slug,
        name: draft.name,
        kind: draft.kind,
        format: draft.format,
        upstreamUrl: draft.upstreamUrl ?? '',
        members: draft.kind === 'virtual' ? draft.members ?? [] : [],
      };
      const next = await updateRepo(repo.id, patch);
      onSaved(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Edit repository" description="Slug changes break existing client configs (settings.xml, .npmrc, etc.). Format/kind changes affect resolution semantics — proceed carefully." />
      <CardBody>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="orb-section-title text-fg-muted">Slug</span>
            <Input
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="orb-section-title text-fg-muted">Name</span>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="orb-section-title text-fg-muted">Kind</span>
            <select
              className={selectClass + ' w-full'}
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value as Repo['kind'] })}
            >
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="orb-section-title text-fg-muted">Format</span>
            <select
              className={selectClass + ' w-full'}
              value={draft.format}
              onChange={(e) => setDraft({ ...draft, format: e.target.value as Repo['format'] })}
            >
              {FORMATS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1 md:col-span-2">
            <span className="orb-section-title text-fg-muted">
              Upstream URL {draft.kind !== 'remote' && <span className="text-fg-subtle">(remotes only — ignored for {draft.kind})</span>}
            </span>
            <Input
              value={draft.upstreamUrl ?? ''}
              onChange={(e) => setDraft({ ...draft, upstreamUrl: e.target.value })}
              placeholder="https://repo.example.com/path"
            />
          </label>

          {draft.kind === 'virtual' && (
            <div className="md:col-span-2 space-y-2">
              <p className="orb-section-title text-fg-muted">Members (resolution order)</p>
              <p className="text-xs text-fg-muted">
                A virtual repo unifies several locals/remotes of the same format under a
                single client URL. Members must share this repo's project and format.
              </p>
              <MemberSelector
                projectId={draft.projectId}
                format={draft.format}
                excludeId={repo.id}
                value={draft.members ?? []}
                onChange={(members) => setDraft({ ...draft, members })}
              />
            </div>
          )}

          {err && (
            <p className="md:col-span-2 text-sm text-danger font-mono">{err}</p>
          )}

          <div className="md:col-span-2 flex items-center gap-2 pt-2">
            <Button variant="primary" type="submit" disabled={busy}>
              Save changes
            </Button>
            <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

interface ReadOnlyHeaderProps {
  readonly repo: Repo;
  readonly onEdit: () => void;
  readonly onTrash: () => void;
}

function ReadOnlyHeader({ repo, onEdit, onTrash }: ReadOnlyHeaderProps) {
  return (
    <div>
      <p className="orb-eyebrow text-accent/80 mb-2">Repository</p>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
          <Box className="h-5 w-5 text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-display font-semibold text-fg leading-tight">{repo.name}</h1>
            <Badge tone={kindTone(repo.kind)}>{repo.kind}</Badge>
            <span className="orb-code text-xs">{repo.format}</span>
          </div>
          <p className="text-fg-muted text-sm mt-2 font-mono">{repo.slug}</p>
          {repo.upstreamUrl && (
            <p className="text-fg-muted text-xs mt-1 truncate">
              Upstream: <span className="text-fg">{repo.upstreamUrl}</span>
            </p>
          )}
          {repo.kind === 'virtual' && (
            <p className="text-fg-muted text-xs mt-1">
              Members: <span className="text-fg">{repo.members?.length ?? 0}</span>
              {repo.members && repo.members.length > 0 && (
                <span className="text-fg-subtle"> (resolved in order)</span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" type="button" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <Button
            variant="ghost"
            type="button"
            onClick={onTrash}
            className="text-fg-muted hover:bg-danger/10 hover:text-danger"
            title="Move this repository to the trash (recoverable for 30 days)"
          >
            <Trash2 className="h-4 w-4" />
            Trash
          </Button>
        </div>
      </div>
    </div>
  );
}

// RetentionCard — v186. Lists retention policies for the repo and
// renders a small create-form below. The endpoint returns 403 for
// non-maintainers; in that case SWR's `error` is set and we hide the
// card entirely so non-owners aren't confused by a button that 403s.
//
// Validation matches the backend:
//   - At least one of max_age_days / keep_per_coord must be > 0.
//   - Both must be positive integers.
// We do client-side first-pass; the server is the authority.
function RetentionCard({ repoID }: { readonly repoID: string }) {
  const { data, error, mutate } = useRepoRetention(repoID);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    name: string;
    max_age_days: string;
    keep_per_coord: string;
    enabled: boolean;
    dry_run: boolean;
  }>({ name: '', max_age_days: '', keep_per_coord: '', enabled: true, dry_run: true });

  // 403 → user is not a maintainer of this project. Hide the card so
  // we don't show a permission-denied for a feature they can't use.
  if (error) return null;

  const togglePolicy = async (p: RetentionPolicy, next: Partial<RetentionPolicy>) => {
    setBusy(true);
    setFormErr(null);
    try {
      const merged: RetentionPolicy = { ...p, ...next, rule: p.rule };
      await updateRepoRetention(repoID, p.name, merged);
      await mutate();
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const removePolicy = async (p: RetentionPolicy) => {
    if (!confirm(`Delete retention policy "${p.name}"?`)) return;
    setBusy(true);
    setFormErr(null);
    try {
      await deleteRepoRetention(repoID, p.name);
      await mutate();
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErr(null);
    const maxAge = draft.max_age_days.trim() ? Number.parseInt(draft.max_age_days, 10) : undefined;
    const keep = draft.keep_per_coord.trim() ? Number.parseInt(draft.keep_per_coord, 10) : undefined;
    if (!draft.name.trim()) {
      setFormErr('Name required');
      return;
    }
    if (maxAge === undefined && keep === undefined) {
      setFormErr('Specify at least one of max_age_days or keep_per_coord');
      return;
    }
    if (maxAge !== undefined && (!Number.isFinite(maxAge) || maxAge <= 0)) {
      setFormErr('max_age_days must be a positive integer');
      return;
    }
    if (keep !== undefined && (!Number.isFinite(keep) || keep <= 0)) {
      setFormErr('keep_per_coord must be a positive integer');
      return;
    }
    setBusy(true);
    try {
      await createRepoRetention(repoID, {
        name: draft.name.trim(),
        rule: {
          ...(maxAge === undefined ? {} : { max_age_days: maxAge }),
          ...(keep === undefined ? {} : { keep_per_coord: keep }),
        },
        enabled: draft.enabled,
        dry_run: draft.dry_run,
      });
      setDraft({ name: '', max_age_days: '', keep_per_coord: '', enabled: true, dry_run: true });
      await mutate();
    } catch (err) {
      setFormErr(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const policies = data ?? [];

  return (
    <Card>
      <CardHeader
        title="Retention"
        description="Per-repo rules that prune old artifacts. Maintainer or higher only. Dry-run logs the deletes without actually removing anything."
      />
      <CardBody>
        {policies.length > 0 ? (
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-3 py-2 font-medium text-fg-muted">Name</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">Max age (d)</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">Keep / coord</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">Enabled</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">Dry-run</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">Last run</th>
                  <th className="px-3 py-2 font-medium text-fg-muted"></th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id ?? p.name} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-2 font-mono text-xs text-fg">{p.name}</td>
                    <td className="px-3 py-2 text-fg-muted text-xs">{p.rule.max_age_days ?? '—'}</td>
                    <td className="px-3 py-2 text-fg-muted text-xs">{p.rule.keep_per_coord ?? '—'}</td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={p.enabled}
                        disabled={busy}
                        onChange={(e) => void togglePolicy(p, { enabled: e.target.checked })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={p.dry_run}
                        disabled={busy}
                        onChange={(e) => void togglePolicy(p, { dry_run: e.target.checked })}
                      />
                    </td>
                    <td className="px-3 py-2 text-fg-muted text-xs whitespace-nowrap">
                      {p.last_run_at ? new Date(p.last_run_at).toLocaleString() : 'never'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        disabled={busy}
                        onClick={() => void removePolicy(p)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-fg-muted text-sm mb-4">No retention policies configured for this repo.</p>
        )}

        <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <label htmlFor="retention-policy-name" className="block text-xs text-fg-muted mb-1">Name</label>
            <Input
              id="retention-policy-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="prune-snapshots"
            />
          </div>
          <div>
            <label htmlFor="retention-policy-max-age" className="block text-xs text-fg-muted mb-1">Max age (days)</label>
            <Input
              id="retention-policy-max-age"
              type="number"
              min={1}
              value={draft.max_age_days}
              onChange={(e) => setDraft({ ...draft, max_age_days: e.target.value })}
              placeholder="30"
            />
          </div>
          <div>
            <label htmlFor="retention-policy-keep-per-coord" className="block text-xs text-fg-muted mb-1">Keep / coord</label>
            <Input
              id="retention-policy-keep-per-coord"
              type="number"
              min={1}
              value={draft.keep_per_coord}
              onChange={(e) => setDraft({ ...draft, keep_per_coord: e.target.value })}
              placeholder="5"
            />
          </div>
          <div className="flex flex-col gap-2 text-xs text-fg-muted">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              />
              {' '}
              Enabled
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.dry_run}
                onChange={(e) => setDraft({ ...draft, dry_run: e.target.checked })}
              />
              {' '}
              Dry-run
            </label>
          </div>
          <div>
            <Button variant="primary" type="submit" disabled={busy}>
              Add policy
            </Button>
          </div>
        </form>

        {formErr && <p className="text-danger text-xs mt-3">{formErr}</p>}
      </CardBody>
    </Card>
  );
}

// DockerImagesCard — roadmap item 137 Phase B. Replaces the generic
// artifact table (raw `manifests/<image>/sha256:<digest>` mirror
// paths) with a human-readable image → tags view for Docker/OCI
// format repos, backed by the Phase A endpoint
// `GET /api/v1/repos/{id}/docker/images`. Each tag row copies a ready
// -to-run `docker pull` command using the browser's own host, mirroring
// the pattern Tokens.tsx uses for CLI snippets.
function shortDigest(d: string): string {
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

function DockerImagesCard({
  repoID,
  projectSlug,
  repoSlug,
}: {
  readonly repoID: string;
  readonly projectSlug: string | undefined;
  readonly repoSlug: string;
}) {
  const { data, isLoading, error } = useDockerImages(repoID);
  const [copied, setCopied] = useState<string | null>(null);
  const host = typeof globalThis === 'undefined' ? 'orbitalreg.example.com' : globalThis.location.host;

  const pullCommand = (imagePath: string, tag: DockerImageTag) =>
    `docker pull ${host}/${projectSlug ?? '<project>'}/${repoSlug}/${imagePath}:${tag.tag}`;

  const onCopy = async (key: string, cmd: string) => {
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard write can fail in non-secure contexts; the row still
      // shows the command text so the operator can select it manually.
    }
  };

  const images = data?.images ?? [];
  const tagCount = images.reduce((n, img) => n + img.tags.length, 0);

  return (
    <Card>
      <CardHeader
        title={`Images (${images.length})`}
        description={isLoading ? 'Loading…' : `${tagCount} tag${tagCount === 1 ? '' : 's'} total. Click a row to copy the pull command.`}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-5 py-3 font-medium text-fg-muted">Image</th>
              <th className="px-5 py-3 font-medium text-fg-muted">Tag</th>
              <th className="px-5 py-3 font-medium text-fg-muted">Size</th>
              <th className="px-5 py-3 font-medium text-fg-muted">Digest</th>
              <th className="px-5 py-3 font-medium text-fg-muted">Updated</th>
              <th className="px-5 py-3 font-medium text-fg-muted w-10"></th>
            </tr>
          </thead>
          <tbody>
            {error && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-danger text-sm">
                  {String(error)}
                </td>
              </tr>
            )}
            {!error && images.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                  <Box className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                  No images pushed yet. Point Docker at this repo with{' '}
                  <code className="orb-code">
                    docker push {host}/{projectSlug ?? '<project>'}/{repoSlug}/&lt;image&gt;:&lt;tag&gt;
                  </code>
                  {'.'}
                </td>
              </tr>
            )}
            {images.map((img) =>
              img.tags.map((t) => {
                const key = `${img.image_path}:${t.tag}`;
                const cmd = pullCommand(img.image_path, t);
                return (
                  <tr
                    key={key}
                    className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40 cursor-pointer"
                    onClick={() => onCopy(key, cmd)}
                    title="Click to copy docker pull command"
                  >
                    <td className="px-5 py-3 font-mono text-xs text-fg break-all max-w-md">
                      {img.image_path}
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone="accent">{t.tag}</Badge>
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                      {bytes(t.size_bytes)}
                    </td>
                    <td className="px-5 py-3">
                      <span className="orb-code text-[10px]" title={t.digest}>
                        {shortDigest(t.digest)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                      {new Date(t.updated_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-fg-subtle text-xs">
                        <Copy className="h-3.5 w-3.5" />
                        {copied === key ? 'Copied' : ''}
                      </span>
                    </td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// StatsTab — v190. Renders the per-repo stats dashboard for the
// "Stats" tab on RepoDetail. Talks to /api/v1/repos/{id}/stats and
// renders four blocks:
//   1. Tile row (storage total / 30-d growth / artifact count / total pulls)
//   2. Daily-pulls SVG sparkline (line + value labels on hover-tooltips)
//   3. Top consumers bar list (only populated for maintainer+ callers)
//   4. Top artifacts by pull count table
// We render charts with hand-rolled SVG instead of pulling in a
// charting library — the dataset is fixed-size (30 daily buckets,
// up to 10 consumers, up to 20 artifacts) and the visual budget is
// "operator skim", not "BI dashboard". Adding recharts to satisfy
// this surface alone would be a 200 KiB JS payload + a build-time
// dependency to maintain; sparkline + bar-chart in raw SVG is
// shorter than the import statement would be.
function StatsTab({ repoID }: { readonly repoID: string }) {
  const { data, error, isLoading } = useRepoStats(repoID);

  if (error) {
    return (
      <Card>
        <CardBody>
          <p className="text-fg-muted text-sm">
            Stats unavailable for this repo (no permission, or repo not found).
          </p>
        </CardBody>
      </Card>
    );
  }
  if (isLoading || !data) {
    return (
      <Card>
        <CardBody>
          <p className="text-fg-muted text-sm">Loading stats…</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Activity (last 30 days)"
          description="Daily pulls, storage totals, and growth. Aggregated server-side from artifact_pulls + artifacts."
        />
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatTile label="Total pulls (30d)" value={data.pulls_total_30d.toLocaleString()} />
            <StatTile label="Total storage" value={bytes(data.storage_total_bytes)} />
            <StatTile label="Storage growth (30d)" value={bytes(data.storage_growth_30d)} />
            <StatTile label="Artifact count" value={data.artifact_count.toLocaleString()} />
          </div>
          <PullsSparkline daily={data.pulls_30d_daily} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Top consumers"
          description="Top 10 pullers in the last 30 days. Only visible to project maintainers and above; lower-privileged callers see this list empty."
        />
        <CardBody>
          {data.top_consumers.length === 0 ? (
            <p className="text-fg-muted text-sm">
              No consumer breakdown available — either this repo had no pulls in the last 30 days, or you don&apos;t have maintainer access on its project.
            </p>
          ) : (
            <ConsumerBars consumers={data.top_consumers} />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Top artifacts by pulls"
          description="The 20 most-downloaded artifacts from this repo over the last 30 days."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Path</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Version</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Size</th>
                <th className="px-5 py-3 font-medium text-fg-muted text-right">Pulls (30d)</th>
              </tr>
            </thead>
            <tbody>
              {data.top_artifacts_by_pulls.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-fg-muted text-sm">
                    No pulls recorded in the last 30 days.
                  </td>
                </tr>
              ) : (
                data.top_artifacts_by_pulls.map((a) => (
                  <tr key={a.artifact_id} className="border-b border-border/60 last:border-0">
                    <td className="px-5 py-3 font-mono text-xs text-fg break-all max-w-md">{a.path}</td>
                    <td className="px-5 py-3 text-fg text-xs">{a.version || '—'}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">{bytes(a.size_bytes)}</td>
                    <td className="px-5 py-3 text-fg text-xs text-right font-mono">{a.pull_count.toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatTile({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <p className="orb-section-title text-fg-muted">{label}</p>
      <p className="text-2xl font-display text-fg mt-1">{value}</p>
    </div>
  );
}

// PullsSparkline — fixed 30-bucket time series rendered as a
// connected polyline over a faint baseline grid. We size off a
// known viewBox (700x140) and let the parent container scale it via
// width=100% so the chart adapts to the page width without
// recomputing on resize.
function xLabelAnchor(idx: number, count: number): 'start' | 'end' | 'middle' {
  if (idx === 0) return 'start';
  if (idx === count - 1) return 'end';
  return 'middle';
}

function PullsSparkline({ daily }: { readonly daily: RepoStats['pulls_30d_daily'] }) {
  if (!daily.length) {
    return <p className="text-fg-muted text-sm">No pull data for this window.</p>;
  }
  const W = 700;
  const H = 140;
  const PAD_L = 28;
  const PAD_R = 8;
  const PAD_T = 12;
  const PAD_B = 24;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const max = Math.max(1, ...daily.map((d) => d.count));
  const stepX = daily.length > 1 ? innerW / (daily.length - 1) : 0;

  const points = daily
    .map((d, i) => {
      const x = PAD_L + i * stepX;
      const y = PAD_T + innerH - (d.count / max) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  // Y-axis labels: show 0, mid, max for orientation. The dataset is
  // small enough that a 3-tick axis communicates the magnitude
  // without crowding.
  const ticks = [0, Math.round(max / 2), max];

  // X-axis labels: show first, middle, last day. More than three
  // makes the labels overlap on narrow viewports.
  const xLabels = daily.length >= 3
    ? [daily[0], daily[Math.floor(daily.length / 2)], daily[daily.length - 1]]
    : daily;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-40 text-accent">
        {/* baseline grid lines */}
        {ticks.map((t, idx) => {
          const y = PAD_T + innerH - (t / max) * innerH;
          return (
            <g key={idx}>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={y}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.12}
                strokeWidth={1}
              />
              <text
                x={PAD_L - 6}
                y={y + 3}
                textAnchor="end"
                fontSize={10}
                fill="currentColor"
                fillOpacity={0.5}
              >
                {t}
              </text>
            </g>
          );
        })}
        {/* connecting line + filled area for visual weight */}
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <polygon
          points={`${PAD_L},${PAD_T + innerH} ${points} ${PAD_L + (daily.length - 1) * stepX},${PAD_T + innerH}`}
          fill="currentColor"
          fillOpacity={0.08}
        />
        {/* day-of-window labels along the bottom */}
        {xLabels.map((d, idx) => {
          const i = daily.indexOf(d);
          const x = PAD_L + i * stepX;
          const anchor = xLabelAnchor(idx, xLabels.length);
          return (
            <text
              key={d.day}
              x={x}
              y={H - 6}
              textAnchor={anchor}
              fontSize={10}
              fill="currentColor"
              fillOpacity={0.5}
            >
              {d.day.slice(5)}
            </text>
          );
        })}
        {/* per-point dots — small to avoid clutter when most points are flat */}
        {daily.map((d, i) => {
          const x = PAD_L + i * stepX;
          const y = PAD_T + innerH - (d.count / max) * innerH;
          return (
            <circle key={d.day} cx={x} cy={y} r={2} fill="currentColor">
              <title>{`${d.day}: ${d.count} pulls`}</title>
            </circle>
          );
        })}
      </svg>
    </div>
  );
}

// ConsumerBars — horizontal bar list. Each row is a labelled
// `principal_email` with a proportional bar (max-width = top
// puller's count). Service-account principals get a neutral
// "service" badge so the operator can tell apart "a CI runner
// pulling on every build" vs. "a developer doing manual installs".
function ConsumerBars({ consumers }: { readonly consumers: RepoStats['top_consumers'] }) {
  const max = Math.max(1, ...consumers.map((c) => c.count));
  return (
    <div className="space-y-2">
      {consumers.map((c) => {
        const pct = Math.round((c.count / max) * 100);
        return (
          <div key={`${c.principal_email}-${c.principal_kind}`} className="flex items-center gap-3">
            <div className="w-56 truncate text-xs text-fg" title={c.principal_email}>
              {c.principal_email}
              {c.principal_kind === 'service_account' && (
                <span className="ml-2 text-[10px] uppercase text-fg-muted">SA</span>
              )}
              {c.principal_kind === 'anonymous' && (
                <span className="ml-2 text-[10px] uppercase text-fg-muted">anon</span>
              )}
            </div>
            <div className="flex-1 h-3 bg-bg-inset rounded-sm overflow-hidden">
              <div
                className="h-full bg-accent/70"
                style={{ width: `${pct}%` }}
                title={`${c.count} pulls`}
              />
            </div>
            <div className="w-16 text-right text-xs text-fg font-mono">{c.count}</div>
          </div>
        );
      })}
    </div>
  );
}

interface RepoHeaderProps {
  readonly loadingRepo: boolean;
  readonly repo: Repo | undefined;
  readonly editing: boolean;
  readonly onSaved: (next: Repo) => void;
  readonly onCancel: () => void;
  readonly onEdit: () => void;
  readonly onTrash: () => void;
}

function RepoHeader({ loadingRepo, repo, editing, onSaved, onCancel, onEdit, onTrash }: RepoHeaderProps) {
  if (loadingRepo) {
    return (
      <Card>
        <CardBody>
          <p className="text-fg-muted">Loading…</p>
        </CardBody>
      </Card>
    );
  }
  if (!repo) return null;
  if (editing) {
    return <EditForm repo={repo} onSaved={onSaved} onCancel={onCancel} />;
  }
  return <ReadOnlyHeader repo={repo} onEdit={onEdit} onTrash={onTrash} />;
}

type Tab = 'contents' | 'stats';

function isTab(v: string | null): v is Tab {
  return v === 'contents' || v === 'stats';
}

export function RepoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: repo, isLoading: loadingRepo, mutate } = useRepoDetail(id);
  const { data: artifacts, isLoading: loadingArts } = useRepoArtifacts(id, 500);
  const [editing, setEditing] = useState(false);

  // Docker-format repos get a readable image/tag view in the Contents
  // tab instead of the raw `manifests/<image>/sha256:<digest>` mirror
  // path (roadmap item 137). projectSlug is resolved the same way the
  // Ollama/Kustomize/OPA browsers do it — from the cached project list,
  // keyed off repo.projectId — since the pull command needs slugs, not
  // the repo UUID.
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );

  // Move the repo to trash (soft-delete). Recoverable from /trash for 30 days;
  // the backend enforces project_admin and 403s otherwise.
  const onTrash = async () => {
    if (!repo) return;
    if (!confirm(`Move repository "${repo.name}" to the trash? It stays recoverable in Trash for 30 days, then the reaper removes it.`)) return;
    try {
      await deleteRepo(repo.id);
      navigate('/repos');
    } catch (e) {
      alert(`Could not move to trash: ${e instanceof Error ? e.message : e}`);
    }
  };

  // Tab state lives in `?tab=` so /repos/<id>?tab=stats is shareable
  // and the browser back-button toggles between the two views.
  // 'contents' is the default; we drop the param when landing on it
  // to keep the URL clean.
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const activeTab: Tab = isTab(rawTab) ? rawTab : 'contents';
  const setTab = (t: Tab) => {
    if (t === 'contents') setParams({}, { replace: false });
    else setParams({ tab: t }, { replace: false });
  };

  return (
    <div className="space-y-6">
      <Link to="/repos" className="inline-flex items-center gap-2 text-fg-muted hover:text-fg text-sm">
        <ArrowLeft className="h-4 w-4" />
        Back to repositories
      </Link>

      <RepoHeader
        loadingRepo={loadingRepo}
        repo={repo}
        editing={editing}
        onSaved={(next) => {
          // SWR's mutate accepts the new value directly; pass it
          // so the page re-renders with the saved row without a
          // round-trip.
          void mutate(next);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
        onEdit={() => setEditing(true)}
        onTrash={onTrash}
      />

      {repo && !editing && (
        <div role="tablist" className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
          <TabButton
            label="Contents"
            Icon={FolderOpen}
            active={activeTab === 'contents'}
            onClick={() => setTab('contents')}
          />
          <TabButton
            label="Stats"
            Icon={BarChart3}
            active={activeTab === 'stats'}
            onClick={() => setTab('stats')}
          />
          {/* Ollama-format repos get a deep-link to the per-model
              browser (roadmap item 38). The dedicated page renders the
              manifest + per-layer breakdown that's compliance-relevant
              for AI bundles. */}
          {repo.format === 'ollama' && (
            <Link
              to={`/repos/${repo.id}/ollama`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              Models
            </Link>
          )}
          {/* Kustomize-format repos get a deep-link to the per-component
              browser (roadmap item 43). Renders the manifest + per-layer
              breakdown plus a kustomization.yaml preview. */}
          {repo.format === 'kustomize' && (
            <Link
              to={`/repos/${repo.id}/kustomize`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              Components
            </Link>
          )}
          {/* OPA-format repos get a deep-link to the per-bundle browser
              (roadmap item 45). Renders bundle catalog + Rego/data preview. */}
          {repo.format === 'opa' && (
            <Link
              to={`/repos/${repo.id}/opa`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              Bundles
            </Link>
          )}
          {/* Agent/Skill-format repos get a deep-link to the package
              browser (roadmap item 149 Phase B). Renders manifest +
              tools/permissions/egress + SKILL.md per package. */}
          {repo.format === 'agentskill' && (
            <Link
              to={`/repos/${repo.id}/agentskill`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              Packages
            </Link>
          )}
        </div>
      )}

      {repo && !editing && activeTab === 'contents' && (
        <>
          <RetentionCard repoID={repo.id} />

          {artifacts && artifacts.length > 0 && (
            <Card>
              <CardHeader title="Quick stats" />
              <CardBody>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="orb-section-title text-fg-muted">Total artifacts</p>
                    <p className="text-2xl font-display text-fg mt-1">{artifacts.length}</p>
                  </div>
                  <div>
                    <p className="orb-section-title text-fg-muted">Total size</p>
                    <p className="text-2xl font-display text-fg mt-1">
                      {bytes(artifacts.reduce((s, a) => s + a.size_bytes, 0))}
                    </p>
                  </div>
                  <div>
                    <p className="orb-section-title text-fg-muted">Unique versions</p>
                    <p className="text-2xl font-display text-fg mt-1">
                      {new Set(artifacts.map((a) => a.version).filter(Boolean)).size}
                    </p>
                  </div>
                  <div>
                    <p className="orb-section-title text-fg-muted">Last upload</p>
                    <p className="text-sm text-fg mt-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-fg-muted" />
                      {new Date(artifacts[0].uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {repo.format === 'docker' ? (
            <DockerImagesCard repoID={repo.id} projectSlug={projectSlug} repoSlug={repo.slug} />
          ) : (
            <Card>
              <CardHeader
                title={`Artifacts (${artifacts?.length ?? 0})`}
                description={loadingArts ? 'Loading…' : 'Newest uploads first. Click a row to copy the path.'}
              />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="px-5 py-3 font-medium text-fg-muted">Path</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">Version</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">Size</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">SHA-256</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">Uploaded</th>
                      {/* v193 — per-artifact detail page; deps tree among other tabs. */}
                      <th className="px-5 py-3 font-medium text-fg-muted w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifacts?.length ? (
                      artifacts.map((a) => (
                        <tr
                          key={a.id}
                          className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40"
                        >
                          <td
                            className="px-5 py-3 font-mono text-xs text-fg break-all max-w-md cursor-pointer"
                            onClick={() => navigator.clipboard.writeText(a.path)}
                            title="Click to copy path"
                          >
                            {a.path}
                          </td>
                          <td className="px-5 py-3 text-fg text-xs">{a.version || '—'}</td>
                          <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">{bytes(a.size_bytes)}</td>
                          <td className="px-5 py-3">
                            <span className="orb-code text-[10px]" title={a.sha256}>
                              {a.sha256.slice(0, 12)}…
                            </span>
                          </td>
                          <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                            {new Date(a.uploaded_at).toLocaleString()}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <Link
                              to={`/artifacts/${a.id}`}
                              className="inline-flex items-center gap-1 text-fg-subtle hover:text-accent text-xs"
                              title="View dependency tree"
                            >
                              <GitBranch className="h-3.5 w-3.5" />
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                          <Download className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                          No artifacts in this repo yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {repo && !editing && activeTab === 'stats' && <StatsTab repoID={repo.id} />}
    </div>
  );
}

function TabButton({ label, Icon, active, onClick }: {
  readonly label: string;
  readonly Icon: typeof FolderOpen;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ' +
        (active
          ? 'bg-bg-elevated text-fg border border-border'
          : 'text-fg-muted hover:text-fg hover:bg-bg-elevated/60')
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
